import { useCallback, useState } from "preact/hooks";
import type { JSX } from "preact";
import { useI18n } from "../lib/i18n";
import { planFor, planLimitOf, usePlan, type PlanFeature, type PlanLimitKind } from "../lib/plan";
import { routeHash } from "../lib/route";

/**
 * What the console says when the plan is in the way.
 *
 * Every plan refusal is the same 402 from the server, so every screen shows
 * the same notice: what was hit, and the one thing that fixes it — which is a
 * link to Billing for the owner, and a sentence for everyone else, because
 * nobody but the owner can act on it (the server refuses them the route).
 */

/**
 * Catches plan refusals for a screen.
 *
 * `catchLimit(err)` returns true when it took the error, so callers keep the
 * shape `catch (err) { if (!catchLimit(err)) fail(err); }` and anything that
 * is not a plan refusal still reaches the normal error line.
 */
export function usePlanLimit(): {
  limit: PlanLimitKind | null;
  catchLimit: (err: unknown) => boolean;
  clearLimit: () => void;
} {
  const [limit, setLimit] = useState<PlanLimitKind | null>(null);
  const catchLimit = useCallback((err: unknown): boolean => {
    const hit = planLimitOf(err);
    if (hit === null) return false;
    setLimit(hit);
    return true;
  }, []);
  const clearLimit = useCallback(() => setLimit(null), []);
  return { limit, catchLimit, clearLimit };
}

export function PlanLimitNotice({ limit }: { limit: PlanLimitKind | null }): JSX.Element | null {
  const { t } = useI18n();
  const { isOwner } = usePlan();
  if (!limit) return null;
  return (
    <div class="alert warn" role="alert">
      <strong>{t.plan.limitTitle}</strong> {t.plan.limits[limit]}{" "}
      {isOwner ? (
        <a href={routeHash({ view: "settings", tab: "billing" })}>{t.plan.upgrade}</a>
      ) : (
        <span>{t.plan.askOwner}</span>
      )}
    </div>
  );
}

/** The short "Team" tag beside a control this plan cannot use. */
export function FeatureHint({ feature }: { feature: PlanFeature }): JSX.Element {
  const { t } = useI18n();
  return <span class="chip plan-chip">{t.plan.needsPlan(t.plan.names[planFor(feature)] ?? planFor(feature))}</span>;
}

/**
 * Whether this screen may use a feature. Unknown plan (an older server, or
 * /agent/me not back yet) reads as allowed: the server is the real gate, and
 * hiding working controls on a guess is worse than a refusal.
 */
export function useFeature(feature: PlanFeature): boolean {
  const { plan } = usePlan();
  return plan?.features[feature] ?? true;
}

/**
 * The whole console, when the workspace is suspended.
 *
 * It carries the links to the screens the suspension does not cover (see
 * route.ts's `blockedBySuspension`), because without the rail they would
 * otherwise be reachable only by typing a URL.
 */
export function SuspendedScreen({
  isOwner,
  isPlatformAdmin,
  onSignOut,
}: {
  isOwner: boolean;
  isPlatformAdmin: boolean;
  onSignOut: () => void;
}): JSX.Element {
  const { t } = useI18n();
  return (
    <div class="empty suspended" role="alert">
      <strong>{t.plan.suspendedTitle}</strong>
      <p>{t.plan.suspendedBody}</p>
      <p>{isOwner ? t.plan.suspendedOwner : t.plan.suspendedOther}</p>
      <div class="actions-row">
        {isOwner ? (
          <a class="btn primary" href={routeHash({ view: "settings", tab: "billing" })}>
            {t.plan.goBilling}
          </a>
        ) : null}
        {isPlatformAdmin ? (
          <>
            <a class="btn" href={routeHash({ view: "customers" })}>
              {t.nav.customers}
            </a>
            <a class="btn" href={routeHash({ view: "earlyAccess" })}>
              {t.nav.earlyAccess}
            </a>
          </>
        ) : null}
        <button class="btn" onClick={onSignOut}>
          {t.nav.signOut}
        </button>
      </div>
    </div>
  );
}
